/**
 * Banner Mode Strategy
 *
 * Display mode strategy for top/bottom banner popups.
 * Banners appear at the top or bottom of the viewport and push page content.
 */

import type {
	DisplayConfig,
	BannerPosition,
	AnimationType,
} from '../../shared/types/popup';
import type { DisplayModeStrategy } from './types';

/**
 * CSS transition duration for body padding animation (in ms).
 * Must match the transition duration in banner.css
 */
const BODY_PADDING_TRANSITION_MS = 300;

/**
 * Strategy for banner display mode (top or bottom).
 */
export class BannerModeStrategy implements DisplayModeStrategy {
	private position: BannerPosition;
	private animationType: AnimationType;

	constructor(
		position: BannerPosition,
		animationType: AnimationType = 'fade'
	) {
		this.position = position;
		this.animationType = animationType;
	}

	/**
	 * Returns CSS classes for banner mode.
	 */
	public getDisplayModeClasses = (): string[] => {
		return [`gp-popup--banner-${this.position}`];
	};

	/**
	 * Apply banner-specific body class.
	 * Banners don't block scrolling - they just add a position-specific class.
	 * For fade animation, we also add a modifier to disable padding transition.
	 */
	public applyBodyState = (): void => {
		document.body.classList.add(`gp-popup-banner-${this.position}-open`);
		// For fade animation, add a no-transition modifier
		if (this.animationType === 'fade') {
			document.body.classList.add('gp-popup-banner-no-transition');
		}
	};

	/**
	 * Remove banner-specific body class and CSS variable.
	 */
	public removeBodyState = (): void => {
		document.body.classList.remove(`gp-popup-banner-${this.position}-open`);
		document.body.classList.remove('gp-popup-banner-no-transition');
		document.body.style.removeProperty('--gp-popup-banner-height');
	};

	/**
	 * Apply sizing to the dialog.
	 * Banners only use height (width is always 100%).
	 * Inner width is handled inside the iframe (passed via PHP in the iframe URL)
	 * so custom CSS in the iframe can add full-width backgrounds.
	 */
	public applyDialogSizing = (
		dialog: HTMLElement,
		config: DisplayConfig
	): void => {
		dialog.style.height = config.popupHeight;
	};

	/**
	 * Banners don't have a clickable backdrop.
	 */
	public shouldCloseOnBackdropClick = (): boolean => {
		return false;
	};

	/**
	 * After the banner is revealed, apply body padding to push page content.
	 */
	public onPopupRevealed = (dialog: HTMLElement): void => {
		const bannerHeight = dialog.offsetHeight;
		document.body.style.setProperty(
			'--gp-popup-banner-height',
			`${bannerHeight}px`
		);
	};

	/**
	 * Before the banner is removed, animate the body padding back to 0.
	 * Returns a promise that resolves after the transition completes.
	 */
	public onPopupRemoving = (): Promise<void> => {
		return new Promise((resolve) => {
			// Set height to 0 to trigger the CSS transition
			document.body.style.setProperty('--gp-popup-banner-height', '0px');

			// Wait for the transition to complete before resolving
			setTimeout(resolve, BODY_PADDING_TRANSITION_MS);
		});
	};
}
